/**
 * Browser-history guard for full-screen web modals.
 *
 * A modal does not create a browser history entry by itself, so Android's back
 * gesture would otherwise leave the current screen. The guard pushes a
 * same-URL entry while the modal is open. Popping that entry dismisses the
 * modal; the next back action is then free to use the router's normal history.
 */

export const WEB_MODAL_HISTORY_KEY = '__warcarpModal'

export interface WebModalHistoryPort {
  readonly state: unknown
  pushState(data: unknown, unused: string): void
  replaceState?(data: unknown, unused: string): void
  back(): void
}

export interface WebModalPopStateEvent {
  readonly state: unknown
}

export interface WebModalPopStatePort {
  addEventListener(type: 'popstate', listener: (event?: WebModalPopStateEvent) => void): void
  removeEventListener(type: 'popstate', listener: (event?: WebModalPopStateEvent) => void): void
}

interface WebModalHistoryLayerOptions {
  history: WebModalHistoryPort
  events: WebModalPopStatePort
  canDismiss: () => boolean
  onDismiss: () => void
  token?: string
}

export interface WebModalHistoryLayer {
  acquire(): void
  release(): void
  /** Pop the owned modal entry, then run route navigation from the resulting
   *  history position so that the pop cannot undo the new route. */
  releaseAndThen(callback: () => void): void
  /** Transfer the current modal history entry to a sibling overlay without
   *  traversing the game route in between. */
  handoff(callback: () => void): void
  dispose(): void
  isActive(): boolean
}

let nextToken = 0
let pendingHandoffToken: string | null = null
// Layers share one browser history. A child popping back to its parent must
// leave that parent (and any deeper ancestors) active.
const activeLayersByHistory = new WeakMap<WebModalHistoryPort, string[]>()

function stateWithMarker(state: unknown, token: string): Record<string, unknown> {
  const base =
    state !== null && typeof state === 'object' && !Array.isArray(state)
      ? (state as Record<string, unknown>)
      : {}
  return { ...base, [WEB_MODAL_HISTORY_KEY]: token }
}

function stateHasMarker(state: unknown, token: string): boolean {
  return (
    state !== null &&
    typeof state === 'object' &&
    !Array.isArray(state) &&
    (state as Record<string, unknown>)[WEB_MODAL_HISTORY_KEY] === token
  )
}

export function createWebModalHistoryLayer({
  history,
  events,
  canDismiss,
  onDismiss,
  token = `modal-${Date.now()}-${++nextToken}`,
}: WebModalHistoryLayerOptions): WebModalHistoryLayer {
  let active = false
  let afterRelease: (() => void) | null = null
  let activeToken = token
  const activeLayers = activeLayersByHistory.get(history) ?? []
  activeLayersByHistory.set(history, activeLayers)
  const activate = () => { active = true; activeLayers.push(activeToken) }
  const deactivate = () => {
    if (!active) return
    active = false
    const index = activeLayers.indexOf(activeToken)
    if (index >= 0) activeLayers.splice(index, 1)
  }

  const acquire = () => {
    if (active) return
    if (
      pendingHandoffToken !== null &&
      stateHasMarker(history.state, pendingHandoffToken)
    ) {
      activeToken = pendingHandoffToken
      pendingHandoffToken = null
      activate()
      return
    }
    activeToken = token
    history.pushState(stateWithMarker(history.state, activeToken), '')
    activate()
  }

  const release = () => {
    if (!active) return
    const ownsCurrentEntry = stateHasMarker(history.state, activeToken)
    deactivate()
    // Remove the same-URL entry when the user closes the modal with its UI.
    // If another navigation already replaced it, leave that navigation alone.
    if (ownsCurrentEntry) history.back()
  }

  const releaseAndThen = (callback: () => void) => {
    // Ignore a second tap while the browser is still traversing away from the
    // same modal entry; the first requested destination remains authoritative.
    if (afterRelease) return
    if (!active) {
      callback()
      return
    }
    const ownsCurrentEntry = stateHasMarker(history.state, activeToken)
    deactivate()
    if (!ownsCurrentEntry) {
      callback()
      return
    }

    // Browser history traversal is asynchronous. Hold navigation until the
    // modal's same-URL entry has actually been removed; navigating first and
    // then calling back() would immediately undo the destination route.
    afterRelease = callback
    history.back()
  }

  const handoff = (callback: () => void) => {
    if (afterRelease) return
    if (active && stateHasMarker(history.state, activeToken)) {
      pendingHandoffToken = activeToken
    }
    deactivate()
    callback()
  }

  const handlePopState = (event?: WebModalPopStateEvent) => {
    if (afterRelease) {
      const callback = afterRelease
      afterRelease = null
      // Let every popstate subscriber (including Expo Router) finish handling
      // the history traversal before presenting the next route or overlay.
      setTimeout(callback, 0)
      return
    }
    if (!active) return
    const ownIndex = activeLayers.indexOf(activeToken)
    // Router subscribers may replace history.state during this same event.
    // The event payload retains the entry we actually popped back to.
    const poppedState = event ? event.state : history.state
    const currentIndex = activeLayers.findIndex(layer => stateHasMarker(poppedState, layer))
    if (ownIndex >= 0 && currentIndex >= ownIndex) {
      // Keep the active top layer's marker on the entry after a router has
      // rewritten its state. Otherwise reopening a child loses this parent on
      // the next dismissal, and closing the parent cannot pop its own entry.
      if (currentIndex === ownIndex && !stateHasMarker(history.state, activeToken)) {
        history.replaceState?.(stateWithMarker(history.state, activeToken), '')
      }
      return
    }
    // The browser has already popped our same-URL entry at this point.
    deactivate()
    if (canDismiss()) {
      onDismiss()
      return
    }

    // Forced card turn-ins are intentionally non-dismissible. Restore the
    // history layer so a browser gesture cannot bypass the game rule.
    acquire()
  }

  events.addEventListener('popstate', handlePopState)

  return {
    acquire,
    release,
    releaseAndThen,
    handoff,
    dispose: () => { deactivate(); events.removeEventListener('popstate', handlePopState) },
    isActive: () => active,
  }
}
